export type ManualAccount = {
  id: string
  first: string
  last: string
  local: string
  domain: string
  signature: string
}

export function validateManualAccount(
  account: ManualAccount,
  opts: {
    allowedDomains: string[]
    allowGlobalSignature?: boolean
    globalSignature?: string
  },
): { isValid: boolean; errors: string[] } {
  const errors: string[] = []
  const first = (account.first || "").trim()
  const last = (account.last || "").trim()
  const local = (account.local || "").trim()
  const domain = (account.domain || "").trim()
  const signature = (account.signature || "").trim()

  if (!first) errors.push("first_required")
  if (!last) errors.push("last_required")
  if (!local) errors.push("local_required")
  const domainLower = domain.toLowerCase()
  if (
    !domainLower ||
    (opts.allowedDomains.length > 0 &&
      !opts.allowedDomains.includes(domainLower))
  )
    errors.push("domain_required")

  // Signature is only required if global signature is not available
  if (!signature && !opts.allowGlobalSignature) {
    errors.push("signature_required")
  }

  return { isValid: errors.length === 0, errors }
}

export function validateAutoMode(params: {
  selectedSendersCount: number
  domainsCount: number
  qty: number
  signature?: string
}): { isValid: boolean; errors: string[] } {
  const errors: string[] = []
  // Provider is always Google Workspace, no need to validate
  if (params.selectedSendersCount <= 0)
    errors.push("select_at_least_one_sender_required")
  if (params.domainsCount <= 0)
    errors.push("select_at_least_one_domain_required")
  if ((params.qty || 0) <= 0) errors.push("email_accounts_per_sender_required")

  return { isValid: errors.length === 0, errors }
}

export function computeNextDisabledReason(args: {
  tab: "domain" | "accounts" | "billing" | "checkout"
  selectedDomainsCount: number
  manualMode: boolean
  selectedSendersCount: number
  accountGenerationDomainsCount: number
  emailAccountsPerSender: number
  signature?: string
  manualAccounts: ManualAccount[]
  previewReadyCount?: number
  previewInvalidCount?: number
  allowedDomains?: string[]
  domainOwner?: any
  billingInformation?: any
  creditCard?: any
  domainOwnerConfirmed?: boolean
}): string | undefined {
  if (args.tab === "checkout") return "already_on_checkout"
  if (args.tab === "domain" && args.selectedDomainsCount === 0)
    return "select_at_least_one_domain"
  if (args.tab === "billing") {
    if (!args.domainOwner) return "domain_owner_required"
    if (!args.billingInformation) return "billing_information_required"
    if (!args.creditCard) return "payment_method_required"
    if (!args.domainOwnerConfirmed) return "domain_owner_confirmation_required"
    return undefined
  }
  if (args.tab !== "accounts") return undefined

  // Provider is always Google Workspace, no need to check

  // Next button should only be enabled if there are previews with status "ready"
  const readyCount = args.previewReadyCount || 0
  if (readyCount === 0) {
    return "preview_ready_required"
  }

  // If there are ready preview rows that are invalid, require all ready rows to be complete
  if ((args.previewInvalidCount || 0) > 0) {
    return "complete_all_accounts_required"
  }

  // Filter out completely blank accounts before validation
  const allowedDomains = (args.allowedDomains || []).map((d) =>
    (d || "").toLowerCase(),
  )
  const nonBlankAccounts = args.manualAccounts.filter((a) => {
    const first = (a.first || "").trim()
    const last = (a.last || "").trim()
    const local = (a.local || "").trim()
    const domain = (a.domain || "").trim()
    const signature = (a.signature || "").trim()
    return first || last || local || domain || signature
  })

  if (!args.manualMode) {
    // Auto mode: validate auto mode requirements
    const { isValid, errors } = validateAutoMode({
      selectedSendersCount: args.selectedSendersCount,
      domainsCount: args.accountGenerationDomainsCount,
      qty: args.emailAccountsPerSender,
      signature: args.signature,
    })
    if (isValid) return undefined
    // Return the first error in order of priority
    return errors[0]
  }

  if (nonBlankAccounts.length === 0) {
    return "complete_at_least_one_account_required"
  }

  const invalidAccounts = nonBlankAccounts.filter((a) => {
    const validation = validateManualAccount(a, {
      allowedDomains,
      allowGlobalSignature: true,
      globalSignature: args.signature,
    })
    return !validation.isValid
  })

  if (invalidAccounts.length > 0) {
    // Check what's missing in the first invalid account
    const firstInvalid = invalidAccounts[0]
    const validation = validateManualAccount(firstInvalid, {
      allowedDomains,
      allowGlobalSignature: true,
      globalSignature: args.signature,
    })
    if (validation.errors.includes("first_required")) return "first_required"
    if (validation.errors.includes("last_required")) return "last_required"
    if (validation.errors.includes("local_required")) return "local_required"
    if (validation.errors.includes("domain_required")) return "domain_required"
    return "complete_all_accounts_required"
  }
  return undefined
}
